/**
 * R-2 — render-time filter for narrative sections whose body is only
 * "data unavailable" sentinel text. Does not mutate persisted reports.
 */
import { normalizeCompoundRefTokens } from '../citationTokens';
import { extractSections } from '../audit/reportTextExtract';
import { scrubInternalSentinels } from './scrubInternalSentinels';
import type { DataSource, SiteRagReport } from '../../types/siteRag';

export const UNAVAILABLE_PATTERNS: RegExp[] = [
  /^\s*\[?\s*data\s+(not\s+)?(unavailable|available)\s*\]?\s*\.?\s*$/i,
  /^\s*\[?\s*unavailable\s*\]?\s*\.?\s*$/i,
  /^\s*\[?\s*n\/?a\s*\]?\s*\.?\s*$/i,
  /^\s*\[?\s*no\s+data\s*\]?\s*\.?\s*$/i,
  /^\s*\[?\s*not\s+applicable\s*\]?\s*\.?\s*$/i,
  /^\s*\[?\s*tbd\s*\]?\s*\.?\s*$/i,
];

// (P0 — no silent data loss) The old `< 20 chars && /unavailable|no data|n\/a|tbd/` heuristic
// OVER-DROPPED legitimately terse content (e.g. "Grid: n/a yet", "Flow ~5 m³/s, tbd final") — a
// substring hit anywhere in a short body is not the same as the body BEING a sentinel. We now drop a
// section ONLY when its WHOLE body matches an anchored UNAVAILABLE_PATTERN, biasing toward keeping
// content. Genuinely-empty terse sections are handled by per-type fallbacks downstream, not here.

export type ReportRenderSection = {
  sectionId: string;
  body: string;
};

type ReportWithSections = SiteRagReport & {
  sections?: ReportRenderSection[];
};

export function isUnavailableBody(body: string): boolean {
  const trimmed = body.trim();
  if (trimmed.length === 0) return true;
  // Whole-body sentinel match ONLY (anchored ^…$) — never a substring hit in terse-but-real prose.
  if (UNAVAILABLE_PATTERNS.some((re) => re.test(trimmed))) return true;
  return false;
}

function collectRenderSections(report: SiteRagReport): ReportRenderSection[] {
  const extended = report as ReportWithSections;
  if (extended.sections && extended.sections.length > 0) {
    return extended.sections.map((s) => ({
      sectionId: s.sectionId,
      body: s.body ?? '',
    }));
  }
  return extractSections(report).map((s) => ({
    sectionId: s.id,
    body: s.content,
  }));
}

export function getUnavailableSectionIds(report: SiteRagReport): string[] {
  return collectRenderSections(report)
    .filter((s) => isUnavailableBody(s.body))
    .map((s) => s.sectionId);
}

export function refMatchesSection(ref: DataSource, sectionId: string): boolean {
  const field = (ref.field ?? '').trim().toLowerCase();
  const sid = sectionId.trim().toLowerCase();
  if (!field || !sid) return false;
  if (field === sid) return true;
  if (field.endsWith(`.${sid}`)) return true;
  if (field.startsWith(`${sid}.`)) return true;
  return false;
}

function buildRefIndexRemap(
  refs: DataSource[],
  droppedRefIndices: Set<number>,
): Map<number, number | null> {
  const map = new Map<number, number | null>();
  let newIdx = 1;
  for (let oldIdx = 0; oldIdx < refs.length; oldIdx++) {
    const old1 = oldIdx + 1;
    if (droppedRefIndices.has(oldIdx)) {
      map.set(old1, null);
    } else {
      map.set(old1, newIdx);
      newIdx += 1;
    }
  }
  return map;
}

function remapRefTokensInText(text: string, remap: Map<number, number | null>): string {
  if (!text) return text;
  // Redhead review wave 3 (defect 1) — a token whose reference row was DROPPED
  // is REMOVED from the text (with its leading whitespace, so no doubled
  // space survives) and logged, never rewritten to a literal `[Ref ?]` / `[?]`
  // placeholder that would ship into customer-facing prose.
  let removed = 0;
  const normalized = normalizeCompoundRefTokens(text);
  const refRemapped = normalized.replace(/[ \t]*\[Ref\s+(\d+)\]/gi, (full, numStr: string) => {
    const old = parseInt(numStr, 10);
    if (!Number.isFinite(old)) return full;
    const next = remap.get(old);
    if (next == null) { removed += 1; return ''; }
    if (next === old) return full;
    return full.replace(/\[Ref\s+\d+\]/i, `[Ref ${next}]`);
  });
  const out = refRemapped.replace(/[ \t]*(?<!\[Ref\s)\[(\d{1,3})\]/g, (full, numStr: string) => {
    const old = parseInt(numStr, 10);
    if (!Number.isFinite(old)) return full;
    const next = remap.get(old);
    if (next == null) { removed += 1; return ''; }
    if (next === old) return full;
    return full.replace(/\[\d{1,3}\]/, `[${next}]`);
  });
  if (removed > 0) {
    console.warn(
      `[filterUnavailableSections] removed ${removed} citation token(s) whose reference row was dropped with its section — not rendered as [?]`,
    );
  }
  return out;
}

function remapReportTextFields(
  report: SiteRagReport,
  remap: Map<number, number | null>,
): SiteRagReport {
  const out = { ...report } as ReportWithSections;
  const r = out as unknown as Record<string, unknown>;

  for (const [key, val] of Object.entries(r)) {
    if (typeof val === 'string' && val.includes('[')) {
      r[key] = remapRefTokensInText(val, remap);
    }
  }

  if (out.sectionContent) {
    const nextContent: Record<string, string> = {};
    for (const [key, val] of Object.entries(out.sectionContent)) {
      nextContent[key] = typeof val === 'string' ? remapRefTokensInText(val, remap) : val;
    }
    out.sectionContent = nextContent;
  }

  if (out.sections) {
    out.sections = out.sections.map((s) => ({
      ...s,
      body: remapRefTokensInText(s.body, remap),
    }));
  }

  if (out.claims) {
    out.claims = out.claims.map((claim) => ({
      ...claim,
      text: remapRefTokensInText(claim.text, remap),
    }));
  }

  const md = (out as SiteRagReport & { markdown?: string }).markdown;
  if (typeof md === 'string' && md.includes('[')) {
    (out as SiteRagReport & { markdown?: string }).markdown = remapRefTokensInText(md, remap);
  }

  return out;
}

function applySectionFilter(
  report: SiteRagReport,
  keptSections: ReportRenderSection[],
  droppedIds: Set<string>,
): SiteRagReport {
  const out = { ...report } as ReportWithSections;
  const r = out as unknown as Record<string, unknown>;

  if (out.sections) {
    out.sections = keptSections.map((s) => ({ ...s }));
  }

  for (const id of droppedIds) {
    if (typeof r[id] === 'string') {
      r[id] = '';
    }
    if (out.sectionContent && id in out.sectionContent) {
      out.sectionContent = { ...out.sectionContent, [id]: '' };
    }
  }

  for (const s of keptSections) {
    if (typeof r[s.sectionId] === 'string') {
      r[s.sectionId] = s.body;
    }
    if (out.sectionContent && s.sectionId in out.sectionContent) {
      out.sectionContent = { ...out.sectionContent, [s.sectionId]: s.body };
    }
  }

  return out;
}

/**
 * Shallow copy with unavailable narrative sections removed, linked
 * references dropped, and inline citation indices renumbered.
 */
export function filterUnavailableSections(report: SiteRagReport): SiteRagReport {
  // Render-time guard: deep-scrub any INTERNAL failure sentinel string
  // ("… unavailable — content failed quality gate. Regenerate report.") to the
  // honest "not assessed; treat as open" notice BEFORE the unavailable-section
  // filter runs, so the section stays visible as an open item instead of being
  // dropped. No persisted mutation (copy-on-write).
  const scrubbed = scrubInternalSentinels(report);
  const sections = collectRenderSections(scrubbed);
  if (sections.length === 0) return scrubbed;
  report = scrubbed;

  const droppedIds = new Set(
    sections.filter((s) => isUnavailableBody(s.body)).map((s) => s.sectionId),
  );
  if (droppedIds.size === 0) return report;

  const keptSections = sections.filter((s) => !droppedIds.has(s.sectionId));
  if (keptSections.length === 0) {
    console.error(
      '[filterUnavailableSections] filter would empty report — returning original',
      { reportId: report.id, dropped: [...droppedIds] },
    );
    return report;
  }

  if (droppedIds.size / sections.length > 0.5) {
    // FAIL-SAFE (P0 — never lose data silently): flagging >half the sections as unavailable almost
    // always signals a generation/extraction hiccup, not a genuinely empty report. Return the
    // UNFILTERED (scrubbed) report so real content is never silently removed, and surface loudly for
    // ops instead of swallowing it. The narrow, normal case (≤50% flagged) still filters below.
    console.error(
      '[filterUnavailableSections] >50% of sections flagged unavailable — returning UNFILTERED report (fail-safe; likely a generation issue, not genuine emptiness)',
      {
        reportId: report.id,
        dropped: droppedIds.size,
        total: sections.length,
        droppedIds: [...droppedIds],
      },
    );
    return report;
  }

  const refs = report.references ?? [];
  const droppedRefIndices = new Set<number>();
  for (let i = 0; i < refs.length; i++) {
    const ref = refs[i]!;
    for (const sid of droppedIds) {
      if (refMatchesSection(ref, sid)) {
        droppedRefIndices.add(i);
        break;
      }
    }
  }

  const filteredRefs = refs.filter((_, i) => !droppedRefIndices.has(i));
  const remap = buildRefIndexRemap(refs, droppedRefIndices);

  let out = applySectionFilter(report, keptSections, droppedIds);
  out = {
    ...out,
    references: filteredRefs.map((ref) => ({ ...ref })),
  };
  out = remapReportTextFields(out, remap);

  return out;
}
